import axios from 'axios';
import Cookies from 'js-cookie';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with auth header
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token + user's selected language to every request. The language
// reaches the backend via Accept-Language so localised responses (e.g. 429
// rate-limit messages) match the UI tone the user is currently seeing.
api.interceptors.request.use((config) => {
  const token = Cookies.get('kahla_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  try {
    const lang = (typeof window !== 'undefined' && localStorage.getItem('language')) || '';
    if (lang && ['en', 'pl', 'de'].includes(lang)) {
      config.headers['Accept-Language'] = lang;
    }
  } catch (e) { /* ignore: localStorage may be blocked in some contexts */ }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      Cookies.remove('kahla_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  checkSetup: () => api.get('/auth/check-setup'),
  setup: (username, password) => api.post('/auth/setup', { username, password }),
  // `identifier` can be either an email (preferred) or a username (legacy).
  // The backend resolves which lookup to use based on whether '@' is present.
  login: (identifier, password) => api.post('/auth/login', { identifier, password }),
  verify: () => api.get('/auth/verify'),
  getSettings: () => api.get('/auth/settings'),
  updateSettings: (data) => api.put('/auth/settings', data),
  getUsage: () => api.get('/auth/usage'),
  toggleSupportAccess: (enabled) => api.put('/auth/support-access', { enabled }),
  getBranding: () => api.get('/auth/branding'),
  updateBranding: (data) => api.put('/auth/branding', data),
  resetBranding: () => api.delete('/auth/branding'),
  uploadBrandingLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/branding/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteBrandingLogo: () => api.delete('/auth/branding/logo'),
  checkInvite: (token) => axios.get(`${API_BASE}/auth/invite/${token}`),
  acceptInvite: (token, username, password) =>
    axios.post(`${API_BASE}/auth/invite/${token}/accept`, { username, password }),
  forgotPassword: (email) => axios.post(`${API_BASE}/auth/forgot-password`, { email }),
  resetPassword: (token, password) => axios.post(`${API_BASE}/auth/reset-password`, { token, password }),
  createRenewalRequest: (period, message) => api.post('/auth/renewal-request', { period, message }),
  getRenewalStatus: () => api.get('/auth/renewal-status'),
};

// Platform texts API
export const platformAPI = {
  getTexts: () => api.get('/platform-texts'),
  updateTexts: (data) => api.put('/platform-texts', data),
  submitAccessRequest: (data) => axios.post(`${API_BASE}/access-requests`, data),
  getAccessRequests: () => api.get('/admin/access-requests'),
  updateAccessRequestStatus: (email, status) => api.put(`/admin/access-requests/${encodeURIComponent(email)}/status`, { status }),
  deleteAccessRequest: (email) => api.delete(`/admin/access-requests/${encodeURIComponent(email)}`),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getDeletedUsers: () => api.get('/admin/users/deleted'),
  getStats: () => api.get('/admin/stats'),
  getSystemHealth: () => api.get('/admin/system-health'),
  listSystemEvents: (limit = 100) => api.get(`/admin/system-events?limit=${limit}`),
  listAuditLog: (q = '', limit = 200) => api.get(`/admin/audit-log?limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  triggerReconciliation: () => api.post('/admin/reconcile'),
  inviteUser: (data) => api.post('/admin/users/invite', data),
  updateUserStatus: (userId, status) =>
    api.put(`/admin/users/${userId}/status`, { status }),
  updateUserLimits: (userId, data) =>
    api.put(`/admin/users/${userId}/limits`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  restoreUser: (userId) => api.post(`/admin/users/${userId}/restore`),
  permanentDeleteUser: (userId) => api.delete(`/admin/users/${userId}/permanent`),
  updateBrandingPermission: (userId, canCustomize) =>
    api.put(`/admin/users/${userId}/branding-permission`, { can_customize_branding: canCustomize }),
  getTemplates: () => api.get('/admin/templates'),
  getTemplate: (id) => api.get(`/admin/templates/${id}`),
  createTemplate: (data) => api.post('/admin/templates', data),
  updateTemplate: (id, data) => api.put(`/admin/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/admin/templates/${id}`),
  uploadLogo: (templateId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/admin/templates/${templateId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: (templateId) => api.delete(`/admin/templates/${templateId}/logo`),
  reassignUserTemplate: (userId, templateId) => api.put(`/admin/users/${userId}/template`, { template_id: templateId }),
  // Plans (Templates/Plans/User split — Phase 2)
  getPlans: () => api.get('/admin/plans'),
  createPlan: (data) => api.post('/admin/plans', data),
  updatePlan: (planId, data) => api.put(`/admin/plans/${planId}`, data),
  deletePlan: (planId) => api.delete(`/admin/plans/${planId}`),
  setUserPlan: (userId, planId) => api.put(`/admin/users/${userId}/plan`, { plan_id: planId }),
  setUserFeatureOverrides: (userId, overrides) =>
    api.put(`/admin/users/${userId}/feature-overrides`, { feature_overrides: overrides }),
  updateUserAccess: (userId, action, days, date) => {
    const body = { action };
    if (days !== undefined) body.days = days;
    if (date !== undefined) body.date = date;
    return api.put(`/admin/users/${userId}/access`, body);
  },
  impersonateUser: (userId) => api.post(`/admin/users/${userId}/impersonate`),
  getRenewalRequests: () => api.get('/admin/renewal-requests'),
  getRenewalHistory: () => api.get('/admin/renewal-history'),
  sendPaymentLink: (requestId, data) => api.put(`/admin/renewal-requests/${requestId}/payment-link`, data),
  markRenewalPaid: (requestId, data) => api.put(`/admin/renewal-requests/${requestId}/mark-paid`, data || {}),
  sendInvoice: (requestId) => api.put(`/admin/renewal-requests/${requestId}/send-invoice`, {}),
  approveRenewal: (requestId, note) => api.put(`/admin/renewal-requests/${requestId}/approve`, { note }),
  declineRenewal: (requestId, note) => api.put(`/admin/renewal-requests/${requestId}/decline`, { note }),
  deleteRenewalRequest: (requestId) => api.delete(`/admin/renewal-requests/${requestId}`),
  checkExpiringUsers: () => api.post('/admin/check-expiring-users'),
};

// Boards API
export const boardsAPI = {
  list: () => api.get('/boards'),
  get: (id) => api.get(`/boards/${id}`),
  create: (data) => api.post('/boards', data),
  update: (id, data) => api.put(`/boards/${id}`, data),
  delete: (id) => api.delete(`/boards/${id}`),
  duplicate: (id) => api.post(`/boards/${id}/duplicate`),
  lock: (id) => api.post(`/boards/${id}/lock`),
  unlock: (id) => api.post(`/boards/${id}/unlock`),
  archive: (id) => api.post(`/boards/${id}/archive`),
  restore: (id) => api.post(`/boards/${id}/restore`),
  notifyClient: (id) => api.post(`/boards/${id}/notify-client`),
  notifyTest: (id) => api.post(`/boards/${id}/notify-test`),
  extend: (id, expirationDays) => api.post(`/boards/${id}/extend`, { expiration_days: expirationDays }),
  exportPDF: (id, options) => api.post(`/boards/${id}/export/pdf`, options),
  exportZip: (id) => api.post(`/boards/${id}/export/zip`, {}, { timeout: 120000 }),
  archiveExportStatus: (id) => api.get(`/boards/${id}/archive-export-status`),
  addNote: (id, text, attachment = null) => api.post(`/boards/${id}/notes`, attachment ? { text, attachment } : { text }),
  editNote: (id, noteId, text) => api.put(`/boards/${id}/notes/${noteId}`, { text }),
  deleteNote: (id, noteId) => api.delete(`/boards/${id}/notes/${noteId}`),
  getNotes: (id) => api.get(`/boards/${id}/notes`),
  markNotesSeen: (id) => api.post(`/boards/${id}/notes/seen`),

  // Private Space — owner-only notes / to-dos / gallery
  privateSpace: {
    addNote: (id, data) => api.post(`/boards/${id}/private/notes`, data),
    updateNote: (id, noteId, data) => api.patch(`/boards/${id}/private/notes/${noteId}`, data),
    deleteNote: (id, noteId) => api.delete(`/boards/${id}/private/notes/${noteId}`),
    addTodo: (id, text) => api.post(`/boards/${id}/private/todos`, { text }),
    updateTodo: (id, todoId, data) => api.patch(`/boards/${id}/private/todos/${todoId}`, data),
    deleteTodo: (id, todoId) => api.delete(`/boards/${id}/private/todos/${todoId}`),
    uploadImage: (id, file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`/boards/${id}/private/gallery`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteImage: (id, imageId) => api.delete(`/boards/${id}/private/gallery/${imageId}`),
    addFromLibrary: (id, libraryImageIds) => api.post(`/boards/${id}/private/gallery/from-library`, { library_image_ids: libraryImageIds }),
  },

  // Final Decision Mode (secure email-based approvals)
  finalDecision: {
    invite: (boardId, email) =>
      api.post(`/boards/${boardId}/final-decision/invite`, { email }),
    status: (boardId) => api.get(`/boards/${boardId}/final-decision/status`),
    resend: (boardId) => api.post(`/boards/${boardId}/final-decision/resend`),
    revoke: (boardId) => api.post(`/boards/${boardId}/final-decision/revoke`),
    disable: (boardId) => api.post(`/boards/${boardId}/final-decision/disable`),
  },

  // Gallery Sections — lightweight visual containers grouping images.
  // Images retain their existing schema; only their `gallery_section_id`
  // links them to a section. Empty / null id = "no section" bucket.
  gallerySections: {
    create: (boardId, data) => api.post(`/boards/${boardId}/gallery-sections`, data),
    update: (boardId, sectionId, data) => api.patch(`/boards/${boardId}/gallery-sections/${sectionId}`, data),
    remove: (boardId, sectionId) => api.delete(`/boards/${boardId}/gallery-sections/${sectionId}`),
    reorder: (boardId, orderIds) => api.post(`/boards/${boardId}/gallery-sections/reorder`, { order: orderIds }),
    assignImage: (boardId, imageId, sectionId) =>
      api.patch(`/boards/${boardId}/images/${imageId}/gallery-section`, { gallery_section_id: sectionId }),
  },
};

// ── Upload timeout ──────────────────────────────────────────────────
// Uploads go through R2 with variable real-world performance (slow
// mobile uplinks, transient proxy stalls, large HEIC re-encodes on the
// backend). Without an explicit timeout, axios defaults to 0 = infinite,
// which is the single biggest cause of stuck "Uploading..." spinners:
// one hanging request blocks the whole batch from ever resolving.
//
// 120s is generous enough to cover the slowest legitimate upload we've
// observed (~80s for a 30MB HEIC on a poor 4G link) while still ensuring
// the batch always reaches a terminal state. The structured failure
// surfaces through useBatchUpload's existing failure path with
// code='upload_timeout' (see api.js error mapping in the upload calls).
const UPLOAD_TIMEOUT_MS = 120000;

// Images API
export const imagesAPI = {
  upload: (boardId, formData) =>
    api.post(`/boards/${boardId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
    }),
  addFromLibrary: (boardId, libraryImageId, sectionLabel, selectionIndex = null) =>
    api.post(`/boards/${boardId}/images/from-library`, {
      library_image_id: libraryImageId,
      section_label: sectionLabel,
      selection_index: selectionIndex,
    }),
  addExternal: (boardId, url, name, sectionLabel, saveToLibrary, selectionIndex = null) =>
    api.post(`/boards/${boardId}/images/external`, {
      url,
      name,
      section_label: sectionLabel,
      save_to_library: saveToLibrary,
      selection_index: selectionIndex,
    }),
  update: (boardId, imageId, data) => 
    api.put(`/boards/${boardId}/images/${imageId}`, data),
  toggleFinal: (boardId, imageId) =>
    api.put(`/boards/${boardId}/images/${imageId}/final`),
  setImageOwnerReply: (boardId, imageId, reply) =>
    api.put(`/boards/${boardId}/images/${imageId}/owner-reply`, { owner_reply: reply }),
  delete: (boardId, imageId) => 
    api.delete(`/boards/${boardId}/images/${imageId}`),
  reorder: (boardId, imageIds) =>
    api.put(`/boards/${boardId}/images/reorder`, { image_ids: imageIds }),
  checkDuplicates: (boardId, payload) =>
    api.post(`/boards/${boardId}/images/check-duplicates`, payload),
};

// Library API
export const libraryAPI = {
  list: (filterType = null, page = 1, pageSize = 60, tag = null, collection = null) => {
    const params = { page, page_size: pageSize };
    if (filterType) params.filter_type = filterType;
    if (tag) params.tag = tag;
    // Collection filter is a first-class organisation layer. The value
    // may be a UUID (single collection), a CSV of UUIDs, the sentinel
    // `all` (or null → omitted), or an empty string to surface only
    // images that don't belong to any collection ("Uncategorized").
    if (collection !== null && collection !== undefined) params.collection = collection;
    return api.get('/library', { params });
  },
  nextOrder: () => api.get('/library/next-order'),
  get: (id) => api.get(`/library/${id}`),
  upload: (formData) =>
    api.post('/library', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
    }),
  checkHash: (contentHash) =>
    api.post('/library/check-hash', { content_hash: contentHash }),
  addExternal: (url, name = null, tags = null) =>
    api.post('/library/external', { url, name, tags }),
  update: (id, data) => api.put(`/library/${id}`, data),
  delete: (id) => api.delete(`/library/${id}`),
  reorder: (imageIds) =>
    api.put('/library/reorder', { image_ids: imageIds }),
  exportTags: (tags) =>
    api.post('/library/export/tags', { tags }, { responseType: 'blob', timeout: 120000 }),
  // ── Collections (higher-level organisation layer) ────────────────
  collections: {
    list: () => api.get('/library-collections'),
    create: (name) => api.post('/library-collections', { name }),
    update: (id, data) => api.put(`/library-collections/${id}`, data),
    remove: (id) => api.delete(`/library-collections/${id}`),
  },
  bulkAssign: (payload) => api.post('/library/bulk-assign', payload),
  // Duplicate detection & cleanup
  duplicates: {
    list: () => api.get('/library/duplicates'),
    dedupe: () => api.post('/library/duplicates/dedupe'),
  },
};

// Labels API
export const labelsAPI = {
  get: () => api.get('/labels'),
  update: (labels) => api.put('/labels', { labels }),
};

// Tags API
export const tagsAPI = {
  getAll: () => api.get('/tags'),
  updateImageTags: (imageId, tags) => api.put(`/library/${imageId}/tags`, { tags }),
};

// Client API (no auth required)
export const clientAPI = {
  getBoard: (shareToken) => 
    axios.get(`${API_BASE}/client/board/${shareToken}`),
  // Approval session — used by /approval/{approval_token} route only
  openApproval: (approvalToken) =>
    axios.get(`${API_BASE}/client/approval/${approvalToken}`),
  approvalToggleFinal: (approvalToken, imageId) =>
    axios.put(`${API_BASE}/client/approval/${approvalToken}/images/${imageId}/final`),
  approvalConfirm: (approvalToken) =>
    axios.post(`${API_BASE}/client/approval/${approvalToken}/confirm`),
  submitReaction: (shareToken, imageId, reaction, comment, participantId) =>
    axios.post(`${API_BASE}/client/board/${shareToken}/images/${imageId}/reaction`, {
      reaction,
      comment,
      participant_id: participantId !== undefined ? participantId : null,
    }),
  uploadInspiration: (shareToken, formData) =>
    axios.post(`${API_BASE}/client/board/${shareToken}/inspirations`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
    }),
  deleteInspiration: (shareToken, inspirationId) =>
    axios.delete(`${API_BASE}/client/board/${shareToken}/inspirations/${inspirationId}`),
  updateInspirationNote: (shareToken, inspirationId, note) =>
    axios.put(`${API_BASE}/client/board/${shareToken}/inspirations/${inspirationId}/note`, { note }),
  addNote: (shareToken, text, participantIdx = null) =>
    axios.post(`${API_BASE}/client/board/${shareToken}/notes`, participantIdx != null ? { text, participant_idx: participantIdx } : { text }),
  editNote: (shareToken, noteId, text) =>
    axios.put(`${API_BASE}/client/board/${shareToken}/notes/${noteId}`, { text }),
  deleteNote: (shareToken, noteId) =>
    axios.delete(`${API_BASE}/client/board/${shareToken}/notes/${noteId}`),
  markNotesSeen: (shareToken) =>
    axios.post(`${API_BASE}/client/board/${shareToken}/notes/seen`),
  toggleFinal: (shareToken, imageId) =>
    axios.put(`${API_BASE}/client/board/${shareToken}/images/${imageId}/final`),
};

// Client Space API — permanent private client records
export const clientsAPI = {
  list: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  remove: (id) => api.delete(`/clients/${id}`),
  tagSuggestions: () => api.get('/clients/tags'),
  exportZip: () => api.post('/clients/export/zip', {}, { timeout: 180000 }),
  exportClientZip: (id) => api.post(`/clients/${id}/export/zip`, {}, { timeout: 180000 }),
  downloadExport: (token) => api.get(`/exports/download/${token}`, { responseType: 'blob', timeout: 180000 }),
  history: {
    list: (clientId, q = '') => api.get(`/clients/${clientId}/history${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    get: (clientId, entryId) => api.get(`/clients/${clientId}/history/${entryId}`),
    create: (clientId, data) => api.post(`/clients/${clientId}/history`, data),
    update: (clientId, entryId, data) => api.put(`/clients/${clientId}/history/${entryId}`, data),
    remove: (clientId, entryId) => api.delete(`/clients/${clientId}/history/${entryId}`),
    addPhoto: (clientId, entryId, file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`/clients/${clientId}/history/${entryId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    removePhoto: (clientId, entryId, photoId) =>
      api.delete(`/clients/${clientId}/history/${entryId}/photos/${photoId}`),
  },
  documents: {
    list: (clientId) => api.get(`/clients/${clientId}/documents`),
    upload: (clientId, file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`/clients/${clientId}/documents/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    linkBoard: (clientId, docId, boardId) =>
      api.patch(`/clients/${clientId}/documents/${docId}`, { board_id: boardId }),
    remove: (clientId, docId) => api.delete(`/clients/${clientId}/documents/${docId}`),
  },
  boards: {
    list: (clientId) => api.get(`/clients/${clientId}/boards`),
    assign: (clientId, boardId) => api.post(`/clients/${clientId}/boards/assign`, { board_id: boardId }),
    unassign: (clientId, boardId) => api.post(`/clients/${clientId}/boards/unassign`, { board_id: boardId }),
  },
};

// Creator Space API — the creator's external memory
export const creatorAPI = {
  capture: (text, extra = {}) => api.post('/creator/capture', { text, ...extra }),
  overview: () => api.get('/creator/overview'),
  items: (params = {}) => api.get('/creator/items', { params }),
  get: (id) => api.get(`/creator/items/${id}`),
  update: (id, data) => api.put(`/creator/items/${id}`, data),
  addEntry: (id, text, kind = 'note') => api.post(`/creator/items/${id}/entries`, { text, kind }),
  decisions: () => api.get('/creator/decisions'),
  timeline: (month) => api.get('/creator/timeline', { params: month ? { month } : {} }),
  remove: (id) => api.delete(`/creator/items/${id}`),
};

// Documents API
export const documentsAPI = {
  list: (boardId) => api.get(`/boards/${boardId}/documents`),
  uploadFile: (boardId, formData) =>
    api.post(`/boards/${boardId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  addLink: (boardId, url, title) =>
    api.post(`/boards/${boardId}/documents/link`, { url, title }),
  delete: (boardId, docId) =>
    api.delete(`/boards/${boardId}/documents/${docId}`),
  rename: (boardId, docId, title) =>
    api.patch(`/boards/${boardId}/documents/${docId}`, { title }),
  clientList: (shareToken) =>
    axios.get(`${API_BASE}/client/board/${shareToken}/documents`),
  markViewed: (shareToken, docId) =>
    axios.post(`${API_BASE}/client/board/${shareToken}/documents/${docId}/view`),
  confirmRead: (shareToken, docId) =>
    axios.post(`${API_BASE}/client/board/${shareToken}/documents/${docId}/confirm`),
};

// AI utilities
export const aiAPI = {
  translateTexts: (sourceLang, targetLang, texts) =>
    api.post('/ai/translate-texts', { source_lang: sourceLang, target_lang: targetLang, texts }),
};

// Analytics — public page-view tracker + admin overview
export const analyticsAPI = {
  track: (page) =>
    axios.post(`${API_BASE}/analytics/track`, { page }).catch(() => {}),
  overview: () => api.get('/admin/analytics/overview'),
};

// Onboarding & Maestra Guide
export const onboardingAPI = {
  state: () => api.get('/onboarding/state'),
  dismissWelcome: () => api.post('/onboarding/dismiss-welcome'),
  seedDemo: () => api.post('/onboarding/seed-demo'),
  restoreDemo: () => api.post('/onboarding/restore-demo'),
  dismissHint: (slug) => api.post('/onboarding/dismiss-hint', { slug }),
  completeStep: (step) => api.post('/onboarding/complete-step', { step }),
  dismissGuidance: (placement) => api.post('/onboarding/dismiss-guidance', { placement }),
  replayGuidance: (placement) => api.post('/onboarding/replay-guidance', { placement }),
};

// Demo Experience — admin-controlled onboarding
export const demoAPI = {
  // Any authenticated user
  getGuidance: () => api.get('/demo/guidance'),
  getOnboardingSteps: (context = 'demo', boardType = null) => {
    // When the caller knows the surface it's running on, pass
    // board_type so the backend can drop steps scoped to the opposite
    // type (Inspiration vs Client). Spotlight uses this on board pages.
    const params = new URLSearchParams({ context });
    if (boardType === 'client' || boardType === 'inspiration') {
      params.set('board_type', boardType);
    }
    return api.get(`/onboarding/steps?${params.toString()}`);
  },
  // Admin only
  adminGetTemplate: () => api.get('/admin/demo/template'),
  adminSetTemplate: (boardId) => api.put('/admin/demo/template', { board_id: boardId }),
  adminRefreshAll: () => api.post('/admin/demo/refresh-all'),
  adminGetTexts: () => api.get('/admin/demo/texts'),
  adminUpdateTexts: (texts) => api.put('/admin/demo/texts', { texts }),
  adminUpdateGuidance: (guidance) => api.put('/admin/demo/guidance', guidance),
  adminListSteps: () => api.get('/admin/onboarding/steps'),
  adminCreateStep: (data) => api.post('/admin/onboarding/steps', data),
  adminUpdateStep: (id, data) => api.put(`/admin/onboarding/steps/${id}`, data),
  adminDeleteStep: (id) => api.delete(`/admin/onboarding/steps/${id}`),
  adminReorderSteps: (order) => api.put('/admin/onboarding/steps/reorder', { order }),
  adminGetTargetPresets: () => api.get('/admin/onboarding/target-presets'),
};

// Admin Launch Atelier — creative direction workspace
// Calls return raw axios responses. UI components destructure `data` themselves.
export const atelierAPI = {
  // Campaigns
  listCampaigns: (includeArchived = false) => api.get(`/admin/atelier/campaigns?include_archived=${includeArchived}`),
  createCampaign: (data) => api.post('/admin/atelier/campaigns', data),
  updateCampaign: (id, data) => api.put(`/admin/atelier/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/admin/atelier/campaigns/${id}`),
  duplicateCampaign: (id) => api.post(`/admin/atelier/campaigns/${id}/duplicate`),
  exportCampaignPdf: (id) =>
    api.get(`/admin/atelier/campaigns/${id}/export-pdf`, { responseType: 'blob' }),

  // Assets
  listAssets: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.type) qs.set('type', params.type);
    if (params.favorite_only) qs.set('favorite_only', 'true');
    if (params.campaign_id) qs.set('campaign_id', params.campaign_id);
    return api.get(`/admin/atelier/assets?${qs.toString()}`);
  },
  createAsset: (data) => api.post('/admin/atelier/assets', data),
  updateAsset: (id, data) => api.put(`/admin/atelier/assets/${id}`, data),
  deleteAsset: (id) => api.delete(`/admin/atelier/assets/${id}`),
  duplicateAsset: (id) => api.post(`/admin/atelier/assets/${id}/duplicate`),
  exportAssetsZip: (ids) =>
    api.post('/admin/atelier/assets/export-zip', { ids }, { responseType: 'blob' }),

  // Brand core
  getBrandCore: () => api.get('/admin/atelier/brand-core'),
  updateBrandCore: (data) => api.put('/admin/atelier/brand-core', data),

  // Brand references (real Maestra assets used to ground image generation)
  listBrandReferences: () => api.get('/admin/atelier/brand-references'),
  uploadBrandReference: (file, { category = 'other', note = '', always_include = false } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    fd.append('note', note);
    fd.append('always_include', always_include ? 'true' : 'false');
    return api.post('/admin/atelier/brand-references', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateBrandReference: (id, data) => api.put(`/admin/atelier/brand-references/${id}`, data),
  deleteBrandReference: (id) => api.delete(`/admin/atelier/brand-references/${id}`),

  // Generation
  generationOptions: () => api.get('/admin/atelier/generate-options'),
  generateFromCampaign: (payload) => api.post('/admin/atelier/generate-from-campaign', payload),
  generate: (payload) => api.post('/admin/atelier/generate', payload),
  generateImage: (payload) => api.post('/admin/atelier/generate-image', payload),
};

// Admin Storage usage
export const storageAPI = {
  getUsage: () => api.get('/admin/storage/usage'),
  refresh: () => api.post('/admin/storage/refresh'),
};

// Admin Database Backups (separate R2 bucket)
export const dbBackupsAPI = {
  list: (limit = 5) => api.get(`/admin/db-backups?limit=${limit}`),
  run: () => api.post('/admin/db-backups/run'),
  download: (key) => api.get(`/admin/db-backups/download?key=${encodeURIComponent(key)}`),
};

// Admin Trash (soft-deleted boards)
export const trashAPI = {
  listBoards: () => api.get('/admin/trash/boards'),
  restoreBoard: (id) => api.post(`/admin/trash/boards/${id}/restore`),
  purgeBoard: (id) => api.delete(`/admin/trash/boards/${id}`),
};

// Founder-led contact flow (user) + Admin → Messages
export const supportAPI = {
  contact: (payload) => api.post('/support/contact', payload),
  listAdmin: (status) => api.get(`/admin/support/messages${status ? `?status=${status}` : ''}`),
  updateAdmin: (id, status) => api.patch(`/admin/support/messages/${id}`, { status }),
};

export const guideAPI = {
  list: () => api.get('/guide/sections'),
  adminList: () => api.get('/admin/guide/sections'),
  adminCreate: (data) => api.post('/admin/guide/sections', data),
  adminUpdate: (id, data) => api.put(`/admin/guide/sections/${id}`, data),
  adminDelete: (id) => api.delete(`/admin/guide/sections/${id}`),
  adminReorder: (order) => api.put('/admin/guide/sections/reorder', { order }),
};

export const legalAPI = {
  // public — used by /impressum, /privacy, /terms
  get: (slug, lang) => api.get(`/legal/${slug}${lang ? `?lang=${lang}` : ''}`),
  // admin — list & edit (raw, including translations)
  adminList: () => api.get('/admin/legal'),
  adminGet: (slug) => api.get(`/admin/legal/${slug}`),
  adminUpdate: (slug, payload) => api.put(`/admin/legal/${slug}`, payload),
};

export const emailLogAPI = {
  list: ({ kind = '', status = '', limit = 100 } = {}) => {
    const params = new URLSearchParams();
    if (kind) params.set('kind', kind);
    if (status) params.set('status', status);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return api.get(`/admin/email-log${qs ? `?${qs}` : ''}`);
  },
  previewCatalog: () => api.get('/admin/email-log/preview-catalog'),
  preview: (kind, lang = 'en') => api.get(
    `/admin/email-log/preview?kind=${encodeURIComponent(kind)}&lang=${encodeURIComponent(lang)}`,
  ),
  sendTestToSelf: (kind, lang = 'en') =>
    api.post('/admin/email-log/preview/send-test', { kind, lang }),
};

export default api;
